import type { PercursoStep, PercursoStepKind, PercursoStop } from 'shared';

// How the backoffice lists a participant's percurso: by month, newest first,
// each day with what happened in it.

const TIME_ZONE = 'Europe/Lisbon';

export interface PercursoMonth {
  /** "YYYY-MM". */
  key: string;
  label: string;
  /** Newest day first. */
  stops: PercursoStop[];
}

/** The stops grouped by month, newest month and newest day first. */
export function monthsNewestFirst(stops: ReadonlyArray<PercursoStop>): PercursoMonth[] {
  return [...stops].reverse().reduce<PercursoMonth[]>((months, stop) => {
    const key = stop.day.slice(0, 7);
    const last = months[months.length - 1];
    if (last && last.key === key) {
      return [...months.slice(0, -1), { ...last, stops: [...last.stops, stop] }];
    }
    return [...months, { key, label: formatMonth(stop.day), stops: [stop] }];
  }, []);
}

export const STEP_KIND_LABEL: Record<PercursoStepKind, string> = {
  visit: 'Visita',
  session: 'Sessão',
  badge: 'Badge',
  equipment: 'Equipamento',
};

/** A "YYYY-MM-DD" day as a Date at noon UTC, so no time zone moves it. */
function dayAsDate(day: string): Date {
  return new Date(`${day}T12:00:00Z`);
}

export function formatMonth(day: string): string {
  const label = dayAsDate(day).toLocaleDateString('pt-PT', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

export function formatStopDay(day: string): string {
  return dayAsDate(day).toLocaleDateString('pt-PT', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('pt-PT', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TIME_ZONE,
  });
}

/** When a step happened: one time for a badge, a span otherwise. */
export function formatStepTime(step: PercursoStep): string {
  if (step.kind === 'badge') return formatTime(step.at);
  return step.endedAt
    ? `${formatTime(step.at)}–${formatTime(step.endedAt)}`
    : `desde ${formatTime(step.at)}`;
}

/** Hours of equipment as "12 h" or "1,5 h". */
export function formatHours(hours: number): string {
  const rounded = Math.round(hours * 10) / 10;
  return `${rounded.toLocaleString('pt-PT')} h`;
}
